import React, { useState, useRef, useEffect } from 'react';
import './MessageInputArea.css';
import EmojiPicker from '../../../components/chatdock/EmojiPicker';
import GifPicker from '../../../components/chatdock/GifPicker';
import { FaKeyboard, FaMicrophone, FaVideo, FaStop, FaCheck, FaMusic, FaFileAlt, FaImage } from 'react-icons/fa';

const MessageInputArea = ({
  messageInput,
  setMessageInput,
  handleSendMessage,
  isRecording,
  isVideoRecording,
  handleStartRecording,
  handleStopRecording,
  handleStartVideoRecording,
  handleStopVideoRecording,
  handleFileUpload,
  handleStartCall,
  handleTyping,
  onEmojiSelect,
  onGifSelect,
  onSendAudio,
  onSendVideo,
  onClearAudio,
  onClearVideo,
  pendingAudioFile,
  pendingVideoFile,
  videoStream,
  isUploading,
  onInputFocus,
  onInputBlur
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const [mode, setMode] = useState('text'); // 'text' | 'audio' | 'video'
  const [showModeMenu, setShowModeMenu] = useState(false);
  const toolsRef = useRef(null);

  useEffect(() => {
    if (!showTools) return;
    const onDocClick = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) {
        setShowTools(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showTools]);

  // If the user switches away from a recording mode, make sure we stop
  useEffect(() => {
    if (mode !== 'audio' && isRecording && handleStopRecording) {
      handleStopRecording();
    }
    if (mode !== 'video' && isVideoRecording && handleStopVideoRecording) {
      handleStopVideoRecording();
    }
  }, [mode, isRecording, isVideoRecording, handleStopRecording, handleStopVideoRecording]);

  const renderModeIcon = () => {
    if (mode === 'audio') return <FaMicrophone />;
    if (mode === 'video') return <FaVideo />;
    return <FaKeyboard />;
  };

  const handleSelectMode = (nextMode) => {
    setMode(nextMode);
    setShowModeMenu(false);
  };

  return (
  <div className="message-input-area">
    <button className="more-btn" title="More" onClick={() => setShowTools(v => !v)}>{showTools ? '▾' : '▴'}</button>
    {showTools && (
      <div ref={toolsRef} className="more-tools-popover">
        <label htmlFor="image-upload" className="tool-btn" title="Send/attach image">
          <FaImage />
        </label>
        <label htmlFor="audio-upload" className="tool-btn" title="Send/attach audio file">
          <FaMusic />
        </label>
        <label htmlFor="video-upload" className="tool-btn" title="Send/attach video file">
          <FaVideo />
        </label>
        <label htmlFor="doc-upload" className="tool-btn" title="Send/attach document">
          <FaFileAlt />
        </label>
        <button className="tool-btn" title="GIF" onClick={() => { setShowTools(false); setShowGifPicker(!showGifPicker); setShowEmojiPicker(false); }}>GIF</button>
        <button className="tool-btn" title="Emoji" onClick={() => { setShowTools(false); setShowEmojiPicker(!showEmojiPicker); setShowGifPicker(false); }}>😀</button>
      </div>
    )}

    <div className="composer-mode-toggle" aria-label="Message input mode">
      <button
        type="button"
        className={`composer-mode-pill single ${mode}`}
        onClick={() => setShowModeMenu(v => !v)}
        aria-haspopup="true"
        aria-expanded={showModeMenu}
        title={mode === 'text' ? 'Text mode' : mode === 'audio' ? 'Audio mode' : 'Video mode'}
      >
        <span className="composer-mode-icon">{renderModeIcon()}</span>
        <span className="composer-mode-caret">▾</span>
      </button>

      {showModeMenu && (
        <div className="composer-mode-menu">
          <button
            type="button"
            className={`composer-mode-menu-item ${mode === 'text' ? 'active' : ''}`}
            onClick={() => handleSelectMode('text')}
            aria-label="Send text message"
            title="Send text message"
          >
            <FaKeyboard />
          </button>
          <button
            type="button"
            className={`composer-mode-menu-item ${mode === 'audio' ? 'active' : ''}`}
            onClick={() => handleSelectMode('audio')}
            aria-label="Record audio message"
            title="Record audio message"
          >
            <FaMicrophone />
          </button>
          <button
            type="button"
            className={`composer-mode-menu-item ${mode === 'video' ? 'active' : ''}`}
            onClick={() => handleSelectMode('video')}
            aria-label="Record video message"
            title="Record video message"
          >
            <FaVideo />
          </button>
        </div>
      )}
    </div>

    <div className={`chat-input-area ${mode === 'audio' ? 'audio-mode' : mode === 'video' ? 'video-mode' : ''}`}>
      {mode === 'text' && (
        <>
          <input
            type="text"
            placeholder="Type a message..."
            value={messageInput}
            onChange={handleTyping}
            onKeyDown={e => { if (e.key === 'Enter') handleSendMessage(); }}
            disabled={isRecording}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
          />
          <button
            className="clear-btn"
            type="button"
            onClick={() => setMessageInput('')}
            disabled={isRecording || !messageInput}
            title="Clear"
          >
            ×
          </button>
          <button className="send-btn" onClick={handleSendMessage} disabled={isRecording}>Send</button>
        </>
      )}

      {mode === 'audio' && (
        <div className="record-mode record-mode-audio">
          <div className="record-mode-main">
            {/* Recording indicator or ready-to-send cue */}
            {isRecording ? (
              <div className="record-visual-bar active">
                <div className="record-wave">
                  <span className="wave-bar" />
                  <span className="wave-bar" />
                  <span className="wave-bar" />
                  <span className="wave-bar" />
                  <span className="wave-bar" />
                  <span className="wave-bar" />
                  <span className="wave-bar" />
                </div>
              </div>
            ) : pendingAudioFile ? (
              <div className="ready-cue">
                <FaCheck size={14} color="#22c55e" />
                <span>Ready to send</span>
              </div>
            ) : (
              <div className="record-visual-bar">
                <div className="record-wave">
                  <span className="wave-bar" />
                  <span className="wave-bar" />
                  <span className="wave-bar" />
                  <span className="wave-bar" />
                  <span className="wave-bar" />
                  <span className="wave-bar" />
                  <span className="wave-bar" />
                </div>
              </div>
            )}
            <button
              type="button"
              className={`record-btn ${isRecording ? 'recording' : ''}`}
              onClick={() => {
                if (isRecording) {
                  handleStopRecording?.();
                } else {
                  handleStartRecording?.();
                }
              }}
              disabled={isVideoRecording}
            >
              {isRecording ? <FaStop /> : <FaMicrophone />}
            </button>
            <button
              type="button"
              className="clear-btn"
              onClick={() => onClearAudio?.()}
              title="Clear recording"
              disabled={!isRecording && !pendingAudioFile || isUploading}
            >
              ×
            </button>
            <button
              className={`send-btn ${pendingAudioFile ? 'ready' : ''}`}
              onClick={() => onSendAudio?.()}
              disabled={isRecording || !pendingAudioFile || isUploading}
            >
              Send
            </button>
          </div>
        </div>
      )}

      {mode === 'video' && (
        <div className="record-mode record-mode-video">
          <div className="record-mode-main">
            {/* Video preview while recording, or ready-to-send cue */}
            {isVideoRecording && videoStream ? (
              <video
                autoPlay
                muted
                playsInline
                className="video-preview"
                ref={videoEl => {
                  if (videoEl && videoStream) {
                    videoEl.srcObject = videoStream;
                  }
                }}
              />
            ) : pendingVideoFile ? (
              <div className="ready-cue">
                <FaCheck size={14} color="#22c55e" />
                <span>Ready to send</span>
              </div>
            ) : (
              <div className="record-visual-bar video">
                <div className="record-wave">
                  <span className="wave-bar" />
                  <span className="wave-bar" />
                  <span className="wave-bar" />
                  <span className="wave-bar" />
                  <span className="wave-bar" />
                  <span className="wave-bar" />
                  <span className="wave-bar" />
                </div>
              </div>
            )}
            <button
              type="button"
              className={`record-btn ${isVideoRecording ? 'recording' : ''}`}
              onClick={() => {
                if (isVideoRecording) {
                  handleStopVideoRecording?.();
                } else {
                  handleStartVideoRecording?.();
                }
              }}
              disabled={isRecording}
            >
              {isVideoRecording ? <FaStop /> : <FaVideo />}
            </button>
            <button
              type="button"
              className="clear-btn"
              onClick={() => onClearVideo?.()}
              title="Clear recording"
              disabled={!isVideoRecording && !pendingVideoFile || isUploading}
            >
              ×
            </button>
            <button
              className={`send-btn ${pendingVideoFile ? 'ready' : ''}`}
              onClick={() => onSendVideo?.()}
              disabled={isVideoRecording || !pendingVideoFile || isUploading}
            >
              Send
            </button>
          </div>
        </div>
      )}

      <input
        type="file"
        id="image-upload"
        accept="image/*,.png,.jpg,.jpeg,.gif,.webp,.bmp,.svg"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />
      <input
        type="file"
        id="audio-upload"
        accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />
      <input
        type="file"
        id="video-upload"
        accept="video/*,.mp4,.mov,.avi,.wmv,.flv,.mkv"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />
      <input
        type="file"
        id="doc-upload"
        accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.zip,.rar"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />
    </div>

    {showEmojiPicker && <EmojiPicker onSelect={(emoji) => { onEmojiSelect?.(emoji); setShowEmojiPicker(false); }} onClose={() => setShowEmojiPicker(false)} />}
    {showGifPicker && <GifPicker onSelect={(gifUrl) => { onGifSelect?.(gifUrl); setShowGifPicker(false); }} onClose={() => setShowGifPicker(false)} />}
  </div>
  );
};

export default MessageInputArea;